'use client';

import { useTranslations } from 'next-intl';
import { type ReactNode, Suspense, useState } from 'react';
import type { CommentsPage } from '@/lib/api/comments';
import type { RelatedEvent, RelatedNews } from '@/lib/api/event/models';
import type { OrderBookSnapshot } from '@/lib/api/order-book/types';
import { ChartContainer } from '@/lib/components/Chart/ChartContainer';
import { EventAwaitingBanner } from '@/lib/components/Event/EventAwaitingBanner';
import { EventAwaitingCard } from '@/lib/components/Event/EventAwaitingCard';
import { EventCommentsTabs } from '@/lib/components/Event/EventCommentsTabs';
import { EventConcludedBanner } from '@/lib/components/Event/EventConcludedBanner';
import { EventDrawBanner } from '@/lib/components/Event/EventDrawBanner';
import { EventDrawCard } from '@/lib/components/Event/EventDrawCard';
import { EventNoWinnerBanner } from '@/lib/components/Event/EventNoWinnerBanner';
import { EventNoWinnerCard } from '@/lib/components/Event/EventNoWinnerCard';
import { EventPausedBanner } from '@/lib/components/Event/EventPausedBanner';
import { EventPausedCard } from '@/lib/components/Event/EventPausedCard';
import { EventRelatedTabs } from '@/lib/components/Event/EventRelatedTabs';
import { EventResultCard } from '@/lib/components/Event/EventResultCard';
import { EventPageHeader } from '@/lib/components/EventPageHeader';
import { EventRules } from '@/lib/components/EventRules';
import { LayoutContainer } from '@/lib/components/Layout/LayoutContainer';
import { OrderBook } from '@/lib/components/OrderBook/OrderBook';
import { OrderFormContainer } from '@/lib/components/OrderForm/OrderFormContainer';
import { OrderFormTriggerMobile } from '@/lib/components/OrderForm/OrderFormTriggerMobile';
import { OutcomesAccordion } from '@/lib/components/Outcomes/OutcomesAccordion';
import { OutcomeTableHeader } from '@/lib/components/Outcomes/OutcomeTableHeader';
import { OutcomeTabsWrapper } from '@/lib/components/Outcomes/OutcomeTabsWrapper';
import { PriceAlertTriggerButton } from '@/lib/components/PriceAlerts';
import { SellParamsInitializer } from '@/lib/components/SellParamsInitializer';
import { TradingDataProvider } from '@/lib/components/Trading/TradingDataContext';
import { OrderBookSocketProvider } from '@/lib/providers/OrderBookSocketProvider';
import { OrderBookStoreProvider } from '@/lib/providers/OrderBookStoreProvider';
import { Market, MarketItem } from '@/lib/types/markets/market';
import { Icon } from '@/lib/ui-kit/icons';
import { isMarketEnded, isOutcomeActive } from '@/lib/utils/outcomePrice';

type EventPageContentProps = {
  market: Market;
  outcomes: MarketItem[];
  initialOrderBooks?: Record<string, OrderBookSnapshot>;
  relatedNews?: RelatedNews[];
  relatedEvents?: RelatedEvent[];
  /** Server-rendered verdict of a settled market (see EventResolutionSummary). */
  resolution?: ReactNode;
  /** First page of comments from SSR, rendered into the HTML. */
  initialComments?: CommentsPage;
};

export function EventPageContent({
  market,
  outcomes,
  initialOrderBooks,
  relatedNews,
  relatedEvents,
  resolution,
  initialComments,
}: EventPageContentProps) {
  const t = useTranslations('event');
  const outcomeIds = outcomes.map((o) => o.id);

  const isEnded = isMarketEnded(market);

  const isAllPaused =
    !isEnded && outcomes.length > 0 && outcomes.every((o) => o.status === 'PAUSED');

  const isAllAwaitingResolution =
    !isEnded &&
    !isAllPaused &&
    outcomes.length > 0 &&
    outcomes.every((o) => o.status === 'AWAITING_RESOLUTION');

  const isAllDraw = outcomes.length > 0 && outcomes.every((o) => o.isDraw === true);

  const isNoWinner =
    !isAllDraw &&
    outcomes.length > 0 &&
    outcomes.every((o) => o.status === 'RESOLVED') &&
    !outcomes.some((o) => o.r?.some((res) => res.isWin));

  const isTradingDisabled = isAllPaused || isAllAwaitingResolution;

  // TradingDataProvider already connects to activity history WebSocket
  return (
    <TradingDataProvider outcomes={outcomes} eventId={market.id}>
      <Suspense fallback={null}>
        <SellParamsInitializer />
      </Suspense>
      <OrderBookStoreProvider initialOrderBooks={initialOrderBooks} outcomeIds={outcomeIds}>
        {/* The SSR order book comes from the ISR cache and can be up to a
            minute old, so the socket's first snapshot must be applied rather
            than discarded as a duplicate of fresh server data. */}
        <OrderBookSocketProvider
          eventId={market.id}
          skipInitialSnapshot={false}
          isEventEnded={isEnded}
        >
          <LayoutContainer className="min-[1024px]:px-4! px-0!">
            <div className="flex max-[1023px]:flex-col min-[1024px]:gap-4">
              <div className="min-w-0 flex-1 pt-4 min-[1024px]:pb-4">
                <EventPageHeader
                  market={market}
                  isFavorite={market.isFavorite}
                  newsCount={relatedNews?.length ?? 0}
                />
                {isAllDraw && <EventDrawBanner />}
                {isNoWinner && <EventNoWinnerBanner />}
                {isEnded && !isAllDraw && !isNoWinner && <EventConcludedBanner />}
                {isAllPaused && <EventPausedBanner />}
                {isAllAwaitingResolution && <EventAwaitingBanner />}
                {resolution}
                <div className="flex flex-col gap-4 pt-4">
                  <div className="relative w-full">
                    <ChartContainer
                      timeControlsExtras={
                        <PriceAlertTriggerButton market={market} outcomes={outcomes} />
                      }
                    />
                  </div>

                  <div className="flex flex-col">
                    {outcomes.length > 1 && <OutcomeTableHeader className="mb-2 px-4" />}
                    {outcomes.length > 1 ? (
                      <OutcomesAccordion
                        outcomes={outcomes}
                        eventId={market.id}
                        isEventConcluded={isEnded}
                      />
                    ) : (
                      <>
                        <OutcomeTabsWrapper
                          outcome={outcomes[0]}
                          showPositionsTabs={true}
                          hideOrderBookTab={true}
                          isEventConcluded={isEnded}
                        />

                        {/* Trading stopped = the book was emptied: nothing to show. */}
                        {!isEnded && isOutcomeActive(outcomes[0]) && (
                          <CollapsibleOrderBookSection
                            label={t('orderBook')}
                            outcomeId={outcomes[0].id}
                          />
                        )}
                      </>
                    )}
                  </div>
                  <div className="mt-16 flex flex-col gap-8 pb-24">
                    <div className="mb-8">
                      <EventRules rule={market.rule} translations={market.translations} />
                    </div>
                    <EventRelatedTabs relatedNews={relatedNews} relatedEvents={relatedEvents} />
                    <EventCommentsTabs
                      marketId={market.id}
                      outcomes={outcomes}
                      initialComments={initialComments}
                    />
                  </div>
                </div>
                {!isEnded && !isTradingDisabled && outcomes.length === 1 ? (
                  <OrderFormTriggerMobile />
                ) : null}
              </div>
              <div
                data-daily-card-avoid
                className="sticky top-[116px] hidden h-fit w-[330px] shrink-0 self-start min-[1024px]:block"
              >
                {isAllDraw ? (
                  <EventDrawCard />
                ) : isNoWinner ? (
                  <EventNoWinnerCard />
                ) : isEnded ? (
                  <EventResultCard outcomes={outcomes} />
                ) : isAllPaused ? (
                  <EventPausedCard />
                ) : isAllAwaitingResolution ? (
                  <EventAwaitingCard />
                ) : (
                  <OrderFormContainer outcomesCount={outcomes.length} />
                )}
              </div>
            </div>
          </LayoutContainer>
        </OrderBookSocketProvider>
      </OrderBookStoreProvider>
    </TradingDataProvider>
  );
}

function CollapsibleOrderBookSection({ label, outcomeId }: { label: string; outcomeId: string }) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="mt-8 flex flex-col">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex cursor-pointer items-center gap-1 border-b border-v2-border pb-3 text-left text-v2-text-primary"
      >
        <span className="font-normal text-[15px] leading-[19px]">{label}</span>
        <Icon
          name="ArrowSmallDown"
          size={24}
          className={`shrink-0 text-v2-text-secondary transition-transform duration-200 ${
            isOpen ? '' : 'rotate-180'
          }`}
        />
      </button>
      {isOpen && (
        <OrderBook
          outcomeId={outcomeId}
          containerClassName="h-[436px]"
          scrollClassName="h-[400px]"
        />
      )}
    </div>
  );
}
